import { useState, useMemo, useEffect } from "react";

/*
 * LeadDashboard  (with status / conversion tracking)
 * ─────────────────────────────────────────────────────────────────────────
 * Analytics + lifecycle tracking over WhatsApp leads (`wa_leads` rows you
 * fetch with dbFetchWaLeads). Drop in as its own page/tab inside SuperAdmin.
 *
 * Each lead row is expected to have:
 *   { id, clicked_at, user_name, mobile, user_city,
 *     provider_name, provider_city, seen, status }
 *
 * `status` is one of: 'new' | 'contacted' | 'converted' | 'lost'
 * (missing/null is treated as 'new').
 *
 * Props:
 *   leads          : array of wa_lead rows         (pass your `waLeads` state)
 *   loading        : bool                          (pass your `waLeadsLoading`)
 *   onRefresh      : () => void                    (re-fetch handler — optional)
 *   onUpdateStatus : (id, status) => Promise<any>  (persist a status change)
 *
 * If onUpdateStatus is omitted, status pills render read-only.
 *
 * ── BACKEND SETUP (do these two things once) ─────────────────────────────
 *
 * 1) Add the column (run in Supabase SQL editor — use the SAME table name
 *    that dbFetchWaLeads reads from; shown here as wa_leads):
 *
 *      alter table wa_leads
 *        add column if not exists status text not null default 'new';
 *
 * 2) Add a write function in supabase.js:
 *
 *      export const dbUpdateWaLeadStatus = async (id, status) => {
 *        const { error } = await supabase
 *          .from("wa_leads")          // <- match dbFetchWaLeads table name
 *          .update({ status })
 *          .eq("id", id);
 *        if (error) throw error;
 *        return true;
 *      };
 *
 *    Then in SuperAdmin: import it and pass
 *      onUpdateStatus={dbUpdateWaLeadStatus}
 */

const C = {
  ink:    "#1C1917",
  muted:  "#78716C",
  faint:  "#A8A29E",
  line:   "#E7E5E0",
  lineLt: "#F3F2EE",
  panel:  "#FFF",
  wash:   "#F7F6F2",
  green:  "#1A6B3C",
  greenBg:"#EAF3DE",
  greenInk:"#27500A",
  wa:     "#25D366",
  todayBg:"#FAFDF7",
  red:    "#A32D2D",
};

const BARS = ["#1A6B3C","#1D9E75","#534AB7","#D85A30","#BA7517","#0C447C","#2D8A52","#A32D2D"];

const STATUSES = [
  { key:"new",       label:"New",       bg:"#F1EFE8", color:"#5F5E5A" },
  { key:"contacted", label:"Contacted", bg:"#E6F1FB", color:"#0C447C" },
  { key:"converted", label:"Converted", bg:"#EAF3DE", color:"#27500A" },
  { key:"lost",      label:"Lost",      bg:"#FCEBEB", color:"#791F1F" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.key, s]));
const normStatus = (l) => STATUS_MAP[l.status] ? l.status : "new";

const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const sameDay = (a, b) =>
  a.getDate()===b.getDate() && a.getMonth()===b.getMonth() && a.getFullYear()===b.getFullYear();

const fmtDateTime = (v) => v
  ? new Date(v).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})
  : "—";
const fmtDate = (v) => v
  ? new Date(v).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})
  : "—";

const PRESETS = [
  { key:"today", label:"Today" },
  { key:"7d",    label:"7 days" },
  { key:"30d",   label:"30 days" },
  { key:"90d",   label:"90 days" },
  { key:"all",   label:"All time" },
];

export default function LeadDashboard({ leads = [], loading = false, onRefresh, onUpdateStatus }) {
  const [preset,     setPreset]     = useState("30d");
  const [from,       setFrom]       = useState("");
  const [to,         setTo]         = useState("");
  const [cityBasis,  setCityBasis]  = useState("customer"); // 'customer' | 'provider'
  const [cityPick,   setCityPick]   = useState("__all");
  const [statusPick, setStatusPick] = useState("__all");
  const [search,     setSearch]     = useState("");
  const [provSort,   setProvSort]   = useState("count");    // 'count' | 'won' | 'recent' | 'name'

  // Local copy so status edits feel instant (optimistic). Re-syncs on refetch.
  const [localLeads, setLocalLeads] = useState(leads);
  const [saving,     setSaving]     = useState({}); // id -> bool
  useEffect(() => { setLocalLeads(leads); }, [leads]);

  const cityField = cityBasis === "customer" ? "user_city" : "provider_city";

  const updateStatus = async (id, status) => {
    const before = localLeads;
    setLocalLeads(ls => ls.map(l => (l.id === id ? { ...l, status } : l)));
    if (!onUpdateStatus) return;
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await onUpdateStatus(id, status);
    } catch (e) {
      setLocalLeads(before); // revert on failure
      alert("Couldn't save status. Check your connection and try again.");
    } finally {
      setSaving(s => { const n = { ...s }; delete n[id]; return n; });
    }
  };

  // ── Resolve active date window ──────────────────────────────────────────
  const range = useMemo(() => {
    if (from || to) {
      const f = from ? startOfDay(from) : null;
      const t = to ? new Date(new Date(to).setHours(23,59,59,999)) : null;
      return { f, t, custom:true };
    }
    const now = new Date();
    if (preset === "all")   return { f:null, t:null, custom:false };
    if (preset === "today") return { f:startOfDay(now), t:null, custom:false };
    const days = preset === "7d" ? 7 : preset === "90d" ? 90 : 30;
    const f = startOfDay(new Date(now.getTime() - (days-1)*86400000));
    return { f, t:null, custom:false };
  }, [preset, from, to]);

  // ── Filters ─────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return localLeads.filter(l => {
      const d = l.clicked_at ? new Date(l.clicked_at) : null;
      if (range.f && (!d || d < range.f)) return false;
      if (range.t && (!d || d > range.t)) return false;
      if (cityPick !== "__all" && (l[cityField] || "—") !== cityPick) return false;
      if (statusPick !== "__all" && normStatus(l) !== statusPick) return false;
      if (q) {
        const hay = `${l.provider_name||""} ${l.user_name||""} ${l.user_city||""} ${l.provider_city||""} ${l.mobile||""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [localLeads, range, cityPick, cityField, statusPick, search]);

  const cityOptions = useMemo(() => {
    const set = new Set();
    localLeads.forEach(l => set.add(l[cityField] || "—"));
    return Array.from(set).sort((a,b)=>a.localeCompare(b));
  }, [localLeads, cityField]);

  // ── KPIs (incl. conversion) ─────────────────────────────────────────────
  const kpis = useMemo(() => {
    const now = new Date();
    const today     = filtered.filter(l => l.clicked_at && sameDay(new Date(l.clicked_at), now)).length;
    const converted = filtered.filter(l => normStatus(l) === "converted").length;
    const lost      = filtered.filter(l => normStatus(l) === "lost").length;
    const fresh     = filtered.filter(l => normStatus(l) === "new").length;
    const provs     = new Set(filtered.map(l => l.provider_name).filter(Boolean)).size;
    const cities    = new Set(filtered.map(l => l.user_city).filter(Boolean)).size;
    const total     = filtered.length;
    const closed    = converted + lost;
    const convRate  = total  ? Math.round((converted/total)*100) : 0;   // of all leads
    const closeRate = closed ? Math.round((converted/closed)*100) : 0;  // of decided leads
    return { total, today, converted, lost, fresh, provs, cities, convRate, closeRate, closed };
  }, [filtered]);

  // ── Status funnel counts ────────────────────────────────────────────────
  const funnel = useMemo(() => {
    const counts = { new:0, contacted:0, converted:0, lost:0 };
    filtered.forEach(l => { counts[normStatus(l)] += 1; });
    return counts;
  }, [filtered]);

  // ── Leads per provider (with win count) ─────────────────────────────────
  const byProvider = useMemo(() => {
    const map = new Map();
    filtered.forEach(l => {
      const key = l.provider_name || "Unknown provider";
      const cur = map.get(key) || { name:key, city:l.provider_city||"—", count:0, won:0, last:null };
      cur.count += 1;
      if (normStatus(l) === "converted") cur.won += 1;
      const t = l.clicked_at ? new Date(l.clicked_at) : null;
      if (t && (!cur.last || t > cur.last)) cur.last = t;
      map.set(key, cur);
    });
    const arr = Array.from(map.values());
    arr.sort((a,b) =>
      provSort === "name"   ? a.name.localeCompare(b.name) :
      provSort === "won"    ? b.won - a.won :
      provSort === "recent" ? (b.last?.getTime()||0) - (a.last?.getTime()||0) :
                              b.count - a.count
    );
    return arr;
  }, [filtered, provSort]);

  // ── Leads per city ──────────────────────────────────────────────────────
  const byCity = useMemo(() => {
    const map = new Map();
    filtered.forEach(l => {
      const key = l[cityField] || "—";
      const cur = map.get(key) || { count:0, won:0 };
      cur.count += 1;
      if (normStatus(l) === "converted") cur.won += 1;
      map.set(key, cur);
    });
    return Array.from(map.entries())
      .map(([city, v]) => ({ city, ...v }))
      .sort((a,b) => b.count - a.count);
  }, [filtered, cityField]);

  // ── Daily trend ─────────────────────────────────────────────────────────
  const trend = useMemo(() => {
    const map = new Map();
    filtered.forEach(l => {
      if (!l.clicked_at) return;
      const k = startOfDay(l.clicked_at).getTime();
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([t, count]) => ({ t, count }))
      .sort((a,b) => a.t - b.t)
      .slice(-30);
  }, [filtered]);

  const maxProv  = Math.max(1, ...byProvider.map(p => p.count));
  const maxCity  = Math.max(1, ...byCity.map(c => c.count));
  const maxTrend = Math.max(1, ...trend.map(t => t.count));

  // ── CSV export (respects filters, includes status) ──────────────────────
  const exportCsv = () => {
    const head = ["Date & Time","Customer","Mobile","Customer City","Provider","Provider City","Status"];
    const rows = filtered.map(l => [
      fmtDateTime(l.clicked_at), l.user_name||"", l.mobile||"",
      l.user_city||"", l.provider_name||"", l.provider_city||"",
      STATUS_MAP[normStatus(l)].label,
    ]);
    const esc = (v) => `"${String(v).replace(/"/g,'""')}"`;
    const csv = [head, ...rows].map(r => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `leads_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const resetCustom = () => { setFrom(""); setTo(""); };

  // ── Style atoms ─────────────────────────────────────────────────────────
  const card  = { background:C.panel, border:`0.5px solid ${C.line}`, borderRadius:12 };
  const label = { fontSize:11, color:C.muted, marginBottom:5, display:"block" };
  const input = { padding:"7px 10px", fontSize:13, borderRadius:8, border:`0.5px solid ${C.line}`, outline:"none", boxSizing:"border-box", background:"#FFF", color:C.ink };
  const ghost = (active) => ({
    padding:"7px 13px", fontSize:13, borderRadius:8, cursor:"pointer",
    border:`0.5px solid ${active ? C.ink : C.line}`,
    background: active ? C.ink : "#FFF",
    color: active ? "#FFF" : C.muted, fontWeight: active ? 500 : 400,
  });

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, gap:12, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontSize:15, fontWeight:600, color:C.ink }}>Lead Dashboard</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
            Track every lead from first contact to paying subscriber.
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={exportCsv} disabled={!filtered.length}
            style={{ ...ghost(false), opacity: filtered.length ? 1 : 0.5 }}>↓ Export CSV</button>
          {onRefresh && <button onClick={onRefresh} style={ghost(false)}>↻ Refresh</button>}
        </div>
      </div>

      {/* Controls */}
      <div style={{ ...card, padding:"14px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", gap:18, flexWrap:"wrap", alignItems:"flex-end" }}>
          <div>
            <span style={label}>Period</span>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {PRESETS.map(p => (
                <button key={p.key} onClick={() => { setPreset(p.key); resetCustom(); }}
                  style={ghost(!range.custom && preset === p.key)}>{p.label}</button>
              ))}
            </div>
          </div>

          <div>
            <span style={label}>Custom range</span>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <input type="date" value={from} max={to || undefined}
                onChange={e => setFrom(e.target.value)} style={input} />
              <span style={{ color:C.faint, fontSize:12 }}>→</span>
              <input type="date" value={to} min={from || undefined}
                onChange={e => setTo(e.target.value)} style={input} />
              {range.custom && (
                <button onClick={resetCustom} style={{ ...ghost(false), padding:"7px 10px", color:C.muted }}>✕</button>
              )}
            </div>
          </div>

          <div>
            <span style={label}>City basis</span>
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={() => { setCityBasis("customer"); setCityPick("__all"); }}
                style={ghost(cityBasis === "customer")}>Customer</button>
              <button onClick={() => { setCityBasis("provider"); setCityPick("__all"); }}
                style={ghost(cityBasis === "provider")}>Provider</button>
            </div>
          </div>

          <div>
            <span style={label}>City</span>
            <select value={cityPick} onChange={e => setCityPick(e.target.value)}
              style={{ ...input, minWidth:150, cursor:"pointer" }}>
              <option value="__all">All cities</option>
              {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <span style={label}>Status</span>
            <select value={statusPick} onChange={e => setStatusPick(e.target.value)}
              style={{ ...input, minWidth:130, cursor:"pointer" }}>
              <option value="__all">All statuses</option>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          <div style={{ flex:1, minWidth:160 }}>
            <span style={label}>Search</span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Provider, customer, mobile…" style={{ ...input, width:"100%" }} />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"3rem", color:C.faint, fontSize:13 }}>Loading…</div>
      ) : localLeads.length === 0 ? (
        <div style={{ ...card, padding:"3rem", textAlign:"center", color:C.faint, fontSize:13 }}>
          No leads yet. They'll appear here when customers tap WhatsApp on a provider page.
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(125px,1fr))", gap:12, marginBottom:16 }}>
            {[
              { k:"Leads (filtered)", v:kpis.total },
              { k:"Today",            v:kpis.today },
              { k:"Converted",        v:kpis.converted, accent:true },
              { k:"Conversion rate",  v:`${kpis.convRate}%`, accent:true, sub:`${kpis.closeRate}% of decided` },
              { k:"New (needs action)", v:kpis.fresh, warn:kpis.fresh>0 },
              { k:"Providers",        v:kpis.provs },
              { k:"Customer cities",  v:kpis.cities },
            ].map(kpi => (
              <div key={kpi.k} style={{ ...card, padding:"14px 16px" }}>
                <div style={{ fontSize:24, fontWeight:600, lineHeight:1.1,
                  color: kpi.warn ? C.red : kpi.accent ? C.green : C.ink }}>{kpi.v}</div>
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{kpi.k}</div>
                {kpi.sub && <div style={{ fontSize:10, color:C.faint, marginTop:2 }}>{kpi.sub}</div>}
              </div>
            ))}
          </div>

          {/* Funnel bar */}
          <div style={{ ...card, padding:"14px 18px", marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.ink, marginBottom:12 }}>Pipeline</div>
            <div style={{ display:"flex", height:14, borderRadius:7, overflow:"hidden", background:C.lineLt }}>
              {STATUSES.map(s => {
                const n = funnel[s.key];
                const pct = kpis.total ? (n/kpis.total)*100 : 0;
                return pct > 0 ? (
                  <div key={s.key} title={`${s.label}: ${n}`} style={{ width:`${pct}%`, background:s.color, opacity:0.85 }} />
                ) : null;
              })}
            </div>
            <div style={{ display:"flex", gap:18, marginTop:10, flexWrap:"wrap" }}>
              {STATUSES.map(s => (
                <span key={s.key} style={{ fontSize:12, color:C.muted, display:"inline-flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:9, height:9, borderRadius:3, background:s.color, opacity:0.85 }} />
                  {s.label} <strong style={{ color:C.ink }}>{funnel[s.key]}</strong>
                </span>
              ))}
            </div>
          </div>

          {/* Providers + cities */}
          <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr", gap:16, marginBottom:16 }}>
            <div style={{ ...card, padding:"16px 18px", minWidth:0 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:600, color:C.ink }}>Leads by provider</div>
                <select value={provSort} onChange={e => setProvSort(e.target.value)}
                  style={{ ...input, padding:"4px 8px", fontSize:12, cursor:"pointer" }}>
                  <option value="count">Most leads</option>
                  <option value="won">Most converted</option>
                  <option value="recent">Most recent</option>
                  <option value="name">A–Z</option>
                </select>
              </div>
              {byProvider.length === 0 ? (
                <div style={{ color:C.faint, fontSize:12, padding:"1rem 0" }}>No leads in this range.</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:11, maxHeight:360, overflowY:"auto" }}>
                  {byProvider.map((p, i) => (
                    <div key={p.name}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4, gap:8 }}>
                        <span style={{ fontSize:13, color:C.ink, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {p.name}
                          <span style={{ fontSize:11, color:C.faint, fontWeight:400, marginLeft:6 }}>{p.city}</span>
                        </span>
                        <span style={{ fontSize:12, color:C.muted, whiteSpace:"nowrap", flexShrink:0 }}>
                          <strong style={{ color:C.ink }}>{p.count}</strong>
                          {p.won > 0 && <span style={{ color:C.greenInk, marginLeft:6 }}>✓{p.won}</span>}
                          <span style={{ color:C.faint, marginLeft:6 }}>{fmtDate(p.last)}</span>
                        </span>
                      </div>
                      <div style={{ height:7, background:C.lineLt, borderRadius:4, overflow:"hidden", position:"relative" }}>
                        <div style={{ width:`${(p.count/maxProv)*100}%`, height:"100%", background:BARS[i % BARS.length], borderRadius:4 }} />
                        {p.won > 0 && (
                          <div style={{ position:"absolute", top:0, left:0, width:`${(p.won/maxProv)*100}%`, height:"100%", background:C.greenInk, borderRadius:4 }} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {byProvider.some(p => p.won > 0) && (
                <div style={{ fontSize:10, color:C.faint, marginTop:10 }}>
                  Dark segment = converted · ✓ = wins
                </div>
              )}
            </div>

            <div style={{ ...card, padding:"16px 18px", minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.ink, marginBottom:14 }}>
                Leads by {cityBasis} city
              </div>
              {byCity.length === 0 ? (
                <div style={{ color:C.faint, fontSize:12, padding:"1rem 0" }}>No leads in this range.</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:11, maxHeight:360, overflowY:"auto" }}>
                  {byCity.map(c => (
                    <div key={c.city}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:13, color:C.ink }}>{c.city}</span>
                        <span style={{ fontSize:12, color:C.muted }}>
                          <strong style={{ color:C.ink }}>{c.count}</strong>
                          {c.won > 0 && <span style={{ color:C.greenInk, marginLeft:6 }}>✓{c.won}</span>}
                          <span style={{ color:C.faint, marginLeft:6 }}>{Math.round((c.count/kpis.total)*100)}%</span>
                        </span>
                      </div>
                      <div style={{ height:7, background:C.lineLt, borderRadius:4, overflow:"hidden" }}>
                        <div style={{ width:`${(c.count/maxCity)*100}%`, height:"100%", background:C.green, borderRadius:4 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trend */}
          <div style={{ ...card, padding:"16px 18px", marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.ink, marginBottom:16 }}>
              Leads over time {trend.length ? `(${trend.length} active day${trend.length>1?"s":""})` : ""}
            </div>
            {trend.length === 0 ? (
              <div style={{ color:C.faint, fontSize:12 }}>No dated leads in this range.</div>
            ) : (
              <div style={{ display:"flex", alignItems:"flex-end", gap:trend.length>20?3:6, height:120, overflowX:"auto" }}>
                {trend.map(t => (
                  <div key={t.t} style={{ flex:"1 0 auto", minWidth:14, display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
                    <span style={{ fontSize:10, color:C.muted }}>{t.count}</span>
                    <div title={`${t.count} on ${fmtDate(t.t)}`}
                      style={{ width:"100%", maxWidth:26, height:`${Math.max(4,(t.count/maxTrend)*84)}px`, background:C.green, borderRadius:"4px 4px 0 0" }} />
                    <span style={{ fontSize:9, color:C.faint, whiteSpace:"nowrap" }}>{fmtDate(t.t)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Lead log with inline status */}
          <div style={{ ...card, overflow:"hidden" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"13px 18px", borderBottom:`0.5px solid ${C.line}`, background:C.wash }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.ink }}>
                Lead log <span style={{ color:C.faint, fontWeight:400 }}>({filtered.length})</span>
              </span>
              {!onUpdateStatus && (
                <span style={{ fontSize:11, color:C.red }}>
                  Status editing disabled — pass onUpdateStatus to enable
                </span>
              )}
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:C.wash, borderBottom:`0.5px solid ${C.line}` }}>
                    {["Date & Time","Customer","Mobile","Customer City","Provider","Provider City","Status"].map(h => (
                      <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:500, fontSize:12, color:C.muted, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding:"2rem", textAlign:"center", color:C.faint, fontSize:13 }}>
                      No leads match these filters.
                    </td></tr>
                  ) : filtered.map((l, i) => {
                    const isToday = l.clicked_at && sameDay(new Date(l.clicked_at), new Date());
                    const st = STATUS_MAP[normStatus(l)];
                    const isSaving = !!saving[l.id];
                    return (
                      <tr key={l.id || i} style={{
                        borderBottom: i < filtered.length-1 ? `0.5px solid ${C.lineLt}` : "none",
                        background: isToday ? C.todayBg : "#FFF" }}>
                        <td style={{ padding:"11px 14px", fontSize:11, color:C.muted, whiteSpace:"nowrap" }}>
                          {isToday && <span style={{ fontSize:10, fontWeight:700, background:C.greenBg, color:C.greenInk, padding:"1px 6px", borderRadius:10, marginRight:6 }}>Today</span>}
                          {fmtDateTime(l.clicked_at)}
                        </td>
                        <td style={{ padding:"11px 14px", fontWeight:500, color:C.ink }}>{l.user_name || "—"}</td>
                        <td style={{ padding:"11px 14px" }}>
                          {l.mobile
                            ? <a href={`https://wa.me/91${l.mobile.replace(/\D/g,"").slice(-10)}`} target="_blank" rel="noopener noreferrer"
                                style={{ color:C.wa, fontWeight:500, textDecoration:"none" }}>💬 {l.mobile}</a>
                            : "—"}
                        </td>
                        <td style={{ padding:"11px 14px", color:C.muted }}>{l.user_city || "—"}</td>
                        <td style={{ padding:"11px 14px", color:C.ink, fontWeight:500 }}>
                          <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:C.wa, flexShrink:0 }} />
                            {l.provider_name || "—"}
                          </span>
                        </td>
                        <td style={{ padding:"11px 14px", color:C.muted }}>{l.provider_city || "—"}</td>
                        <td style={{ padding:"9px 14px" }}>
                          {onUpdateStatus ? (
                            <select
                              value={normStatus(l)}
                              disabled={isSaving}
                              onChange={e => updateStatus(l.id, e.target.value)}
                              style={{
                                fontSize:12, fontWeight:600, padding:"4px 8px", borderRadius:20,
                                border:`0.5px solid ${st.color}33`, background:st.bg, color:st.color,
                                cursor: isSaving ? "wait" : "pointer", outline:"none",
                                opacity: isSaving ? 0.6 : 1, appearance:"none", WebkitAppearance:"none",
                              }}>
                              {STATUSES.map(s => (
                                <option key={s.key} value={s.key} style={{ background:"#FFF", color:C.ink }}>{s.label}</option>
                              ))}
                            </select>
                          ) : (
                            <span style={{ fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, background:st.bg, color:st.color }}>
                              {st.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
